import { withLocale, splitLocalePath } from "../i18n/localePath";
import { Paths } from "./paths";

export type PathKey = keyof typeof Paths;

/**
 * Public pages that should be crawled, indexed, and listed in the sitemap.
 */
export const PUBLIC_INDEXABLE = [
  "HOME",
  "ABOUT_US",
  "ABOUT_FOUNDER",
  "OUR_MISSION",
  "CONTACT",
  "COMMUNITY",
  "FOR_BUSINESS",
  "MCP",
  "STARTUPS",
  "REFERRAL",
  "PRIVACY_POLICY",
  "TERMS_AND_CONDITIONS",
  "DISCLOSURES",
  "SHARIAH_SCREENING_METHODOLOGY",
  "STOCK_SCREENER",
  "ETF_SCREENER",
  "STOCK_ANALYSES",
  "TOP_HALAL_STOCKS",
  "SCREENING",
  "INSIDER_BY_SECTOR",
  "DIVIDEND",
  "SUMMARY",
  "MARKET_NEWS",
  "READY_MADE_PORTFOLIO",
  "ARTICLES",
  "ARTICLE_DETAIL",
  "ACADEMY",
  "WEBINARS",
  "COURSE_INFO_ID",
  "WEBINAR_DETAIL_ID",
  "INVESTING_CALCULATOR",
  "ZAKAT_CALCULATOR",
  "PURIFICATION_CALCULATOR",
  "BACKTESTING",
  "AKINDA_AI",
  "SYMBOL",
  "ETF",
  "PARTNERS",
] as const satisfies readonly PathKey[];

/**
 * Public but worthless in search: auth funnels, transient result pages, and
 * anything that renders differently per visitor. Crawlable, never indexed.
 */
export const PUBLIC_NOINDEX = [
  "LOGIN",
  "SIGN_UP",
  "VERIFY_EMAIL",
  "FORGOT_PASSWORD",
  "CHECK_YOUREMAIL",
  "SET_NEW_PASSWORD",
  "PASSWORD_RESET",
  "GOOGLE_EXTERNAL_AUTH",
  "PUBLIC_PORTFOLIO",
  // Paid content: logged-out visitors (and crawlers) only get the login
  // lock, so the gallery is indexed but the details aren't.
  "READY_MADE_PORTFOLIO_ID",
  // Staff tool: build a ready-made portfolio's holdings from a screener filter.
  "READY_MADE_PORTFOLIO_STOCKS",
  "SUBSCRIPTION_RESULT",
  "SUBSCRIBE_PLAN_TYPE",
  "PAYMENT_DETAILS",
  "REFERRAL_CODE",
  "GAMES",
  "FINANCE",
  "STOCK_REPORT",
  "PORTFOLIO_TRACKING",
  "PORTFOLIO_BACKTESTING",
  "MY_PORTFOLIO",
  "COURSE_DASHBOARD_ID",
  "COMMUNITY_FEED",
  "COMMUNITY_POST",
  "COMMUNITY_PROFILE",
  "COMMUNITY_SEARCH",
] as const satisfies readonly PathKey[];

/**
 * Behind auth. Disallowed in robots.txt AND served with noindex, since
 * robots.txt alone does not remove already-indexed URLs.
 */
export const AUTHENTICATED = [
  "DASHBOARD",
  "PROFILE",
  "PERSONAL_INFO",
  "SETTINGS",
  "PORTFOLIOS",
  "MY_CARDS",
  "REPORTS",
  "REPORT_DETAIL_ID",
  "SUBSCRIPTIONS",
  "SUBSCRIPTION_MATRIX",
  "WEBSITE_ANALYTICS",
  "SUPPORT_TICKETS",
  "NOTIFICATIONS",
  "NOTIFICATIONS_CREATE",
  "NOTIFICATIONS_DETAILS_ID",
  "NOTIFICATIONS_SETTINGS",
  "USERS",
  "USERS_CREATE",
  "USERS_DETAILS_ID",
  "ADMIN_ROLES",
  "ADMIN_ROLES_CREATE",
  "ADMIN_ROLES_DETAILS_ID",
  "ADMIN_REFERRAL",
  "REPORTS_CREATE",
  "REPORTS_DETAILS_ID",
  "CREATE_REPORT",
  "CREATE_REPORT_ID",
  "EDIT_REPORT_ID",
  "ARTICLES_CREATE",
  "ARTICLES_CREATE_ID",
  "ARTICLE_CATEGORIES",
  "ARTICLE_CATEGORIES_CREATE",
  "ARTICLE_CATEGORIES_CREATE_ID",
  "WEBINARS_CREATE",
  "WEBINARS_CREATE_ID",
  "COURSES_CREATE",
  "COURSES_CREATE_ID",
  "COURSES_DETAIL_ID",
  "COURSES_STATS_ID",
  "PROMOCODES",
  "PROMOCODES_CREATE",
  "PROMOCODES_CREATE_ID",
  "PROMOCODES_DETAILS_ID",
  "ONBOARDING_QUESTIONS",
  "ONBOARDING_QUESTIONS_CREATE",
  "ONBOARDING_QUESTIONS_CREATE_ID",
  "ONBOARDING_STATS",
  "COMMUNITY_BLOCKED",
] as const satisfies readonly PathKey[];

/** URL patterns that carry route params, i.e. cannot be listed literally. */
export const isDynamic = (pattern: string) => pattern.includes(":");

/**
 * Maps a route to the PAGE_META_TAGS key that supplies its metadata.
 *
 * Every param-free PUBLIC_INDEXABLE route must appear here;
 * scripts/verify-seo.mjs fails the build otherwise. Parameterised routes get
 * their metadata from the page component, which has the loaded record.
 */
export const PAGE_SEO_KEY = {
  HOME: "home",
  ABOUT_US: "about",
  ABOUT_FOUNDER: "aboutFounder",
  OUR_MISSION: "ourMission",
  CONTACT: "contact",
  COMMUNITY: "community",
  FOR_BUSINESS: "forBusiness",
  MCP: "mcp",
  STARTUPS: "startups",
  REFERRAL: "referral",
  PRIVACY_POLICY: "privacyPolicy",
  TERMS_AND_CONDITIONS: "termsAndConditions",
  DISCLOSURES: "disclosures",
  SHARIAH_SCREENING_METHODOLOGY: "shariahScreeningMethodology",
  STOCK_SCREENER: "stockScreener",
  ETF_SCREENER: "etfScreener",
  STOCK_ANALYSES: "stockAnalyses",
  SCREENING: "stockAnalyses",
  INSIDER_BY_SECTOR: "stockAnalyses",
  DIVIDEND: "stockAnalyses",
  TOP_HALAL_STOCKS: "topHalalStocks",
  SUMMARY: "summary",
  MARKET_NEWS: "marketNews",
  READY_MADE_PORTFOLIO: "readyMadePortfolio",
  ARTICLES: "articles",
  ACADEMY: "academy",
  WEBINARS: "webinars",
  INVESTING_CALCULATOR: "investingCalculator",
  ZAKAT_CALCULATOR: "zakatCalculator",
  PURIFICATION_CALCULATOR: "purificationCalculator",
  BACKTESTING: "backtesting",
  AKINDA_AI: "akindaAi",
} as const satisfies Partial<Record<PathKey, string>>;

/**
 * Routes that serve byte-identical content to another route, mapped to the
 * URL that owns it.
 *
 * /stock-analyses, /screening, /insider-by-sector, /dividend,
 * /portfolio-backtesting and /portfolio-tracking all render the same
 * StockAnalyses component, which does not read the pathname. A
 * self-referencing canonical on a true duplicate tells Google to index six
 * copies of one page, so the duplicates point at the original instead.
 *
 * Give a route distinct content and its own metadata to graduate it out of
 * this map.
 */
export const CANONICAL_ALIAS = {
  SCREENING: "STOCK_ANALYSES",
  INSIDER_BY_SECTOR: "STOCK_ANALYSES",
  DIVIDEND: "STOCK_ANALYSES",
  PORTFOLIO_BACKTESTING: "STOCK_ANALYSES",
  PORTFOLIO_TRACKING: "STOCK_ANALYSES",
} as const satisfies Partial<Record<PathKey, PathKey>>;

const ALIAS_BY_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(CANONICAL_ALIAS).map(([from, to]) => [
    Paths[from as PathKey],
    Paths[to as PathKey],
  ]),
);

/**
 * The canonical path for a pathname, resolving duplicate routes to the URL
 * that owns the content. Returns the input for everything else.
 */
export const canonicalPathFor = (pathname: string): string => {
  const { locale, pathname: bare } = splitLocalePath(pathname);
  const normalized = bare.replace(/\/+$/, "") || "/";
  const target = ALIAS_BY_PATH[normalized] ?? normalized;
  return locale ? withLocale(target, locale) : target;
};
